// src/services/dataAdapters/MockDataAdapter.js
// In-memory DataAdapter used for demos, unit tests, and the 'mock' DataSource type.
//
// Default schema and data mimic an AML context (customers, transactions, alerts).
// Callers can inject custom data/schema via the constructor config object:
//   new MockDataAdapter({ data: {...}, schema: {...} })

import DataAdapter from './DataAdapter';

// ─────────────────────────────────────────────────────────────────────────────
// Default schema (column definitions per table)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_SCHEMA = {
  customers: [
    { name: 'id',           type: 'text',    nullable: false },
    { name: 'name',         type: 'text',    nullable: false },
    { name: 'country_code', type: 'text',    nullable: true  },
    { name: 'risk_score',   type: 'numeric', nullable: true  },
    { name: 'status',       type: 'text',    nullable: true  },
  ],
  transactions: [
    { name: 'id',           type: 'text',    nullable: false },
    { name: 'customer_id',  type: 'text',    nullable: false },
    { name: 'amount',       type: 'numeric', nullable: false },
    { name: 'currency',     type: 'text',    nullable: true  },
    { name: 'date',         type: 'text',    nullable: true  },
    { name: 'country_code', type: 'text',    nullable: true  },
  ],
  alerts: [
    { name: 'id',          type: 'text', nullable: false },
    { name: 'customer_id', type: 'text', nullable: false },
    { name: 'alert_type',  type: 'text', nullable: true  },
    { name: 'severity',    type: 'text', nullable: true  },
    { name: 'created_at',  type: 'text', nullable: true  },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Default in-memory data rows
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_DATA = {
  customers: [
    { id: 'c1', name: 'Acme Corp',    country_code: 'SE', risk_score: 0.2, status: 'active'    },
    { id: 'c2', name: 'Globex Ltd',   country_code: 'DE', risk_score: 0.7, status: 'active'    },
    { id: 'c3', name: 'Umbrella SA',  country_code: 'CH', risk_score: 0.9, status: 'suspended' },
    { id: 'c4', name: 'Initech AB',   country_code: 'SE', risk_score: 0.1, status: 'active'    },
    { id: 'c5', name: 'Vandelay Inc', country_code: 'US', risk_score: 0.5, status: 'active'    },
  ],
  transactions: [
    { id: 't1', customer_id: 'c1', amount: 5000,   currency: 'SEK', date: '2024-01-10', country_code: 'SE' },
    { id: 't2', customer_id: 'c2', amount: 120000, currency: 'EUR', date: '2024-01-11', country_code: 'DE' },
    { id: 't3', customer_id: 'c3', amount: 890000, currency: 'CHF', date: '2024-01-12', country_code: 'CH' },
    { id: 't4', customer_id: 'c1', amount: 2500,   currency: 'SEK', date: '2024-01-13', country_code: 'SE' },
    { id: 't5', customer_id: 'c4', amount: 50000,  currency: 'SEK', date: '2024-01-14', country_code: 'SE' },
  ],
  alerts: [
    { id: 'a1', customer_id: 'c2', alert_type: 'high_value',   severity: 'medium', created_at: '2024-01-11' },
    { id: 'a2', customer_id: 'c3', alert_type: 'country_risk', severity: 'high',   created_at: '2024-01-12' },
    { id: 'a3', customer_id: 'c3', alert_type: 'high_value',   severity: 'high',   created_at: '2024-01-12' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns only rows that match ALL key/value pairs in filter (exact-match).
 * An empty or missing filter returns all rows unchanged.
 */
function applyFilter(rows, filter) {
  if (!filter || Object.keys(filter).length === 0) return rows;
  return rows.filter(row =>
    Object.entries(filter).every(([key, val]) => row[key] === val)
  );
}

/**
 * Returns rows projected to only the specified columns.
 * If columns is empty/undefined, rows are returned as-is.
 */
function applyColumns(rows, columns) {
  if (!columns || columns.length === 0) return rows;
  return rows.map(row => {
    const out = {};
    for (const col of columns) {
      if (Object.prototype.hasOwnProperty.call(row, col)) {
        out[col] = row[col];
      }
    }
    return out;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// MockDataAdapter
// ─────────────────────────────────────────────────────────────────────────────

class MockDataAdapter extends DataAdapter {
  /**
   * @param {{ data?: object, schema?: object }} config
   *   data   — map of tableName → row[]  (overrides DEFAULT_DATA)
   *   schema — map of tableName → column[] (overrides DEFAULT_SCHEMA)
   */
  constructor(config = {}) {
    super();
    this._data   = config.data   || DEFAULT_DATA;
    this._schema = config.schema || DEFAULT_SCHEMA;
  }

  get type() {
    return 'mock';
  }

  async ping() {
    return { success: true, latencyMs: 0 };
  }

  async listTables() {
    return Object.keys(this._schema);
  }

  async listColumns(table) {
    return this._schema[table] ? [...this._schema[table]] : [];
  }

  async query({ table, columns, filter, limit } = {}) {
    const rows = this._data[table];
    if (!rows) return [];
    let result = applyFilter(rows, filter);
    if (limit && limit > 0) result = result.slice(0, limit);
    return applyColumns(result, columns);
  }

  async count({ table, filter } = {}) {
    const rows = this._data[table];
    if (!rows) return 0;
    return applyFilter(rows, filter).length;
  }

  async schemaCapabilities() {
    return {
      canListTables:      true,
      canListColumns:     true,
      canIntrospectTypes: true,
    };
  }
}

export default MockDataAdapter;
